/**
 * The work-state system (docs/design-system/language/status.md, "Work states"): one canonical
 * set of states for any unit of work (a session, a workflow run, a landing, a checkout or
 * branch, a mission), so a queue, a list and a summary bar read the same on every page.
 *
 * Order is the order of attention: what needs a person first, then what broke, then what is
 * moving, then what waits, then what is finished or quiet. Finished and quiet groups start
 * collapsed.
 */
export type WorkState = 'needs-you' | 'failed' | 'running' | 'verifying' | 'waiting-for-cpu' | 'queued' | 'landed' | 'stale';

export const WORK_STATES: readonly WorkState[] = ['needs-you', 'failed', 'running', 'verifying', 'waiting-for-cpu', 'queued', 'landed', 'stale'];

/** The colour role each state draws with: a status token, or neutral grey. */
export type WorkTone = 'attention' | 'danger' | 'working' | 'success' | 'neutral';

export interface WorkStateMeta {
  /** The default label; pages may say "Passed" or "Done" where that is the word. */
  label: string;
  tone: WorkTone;
  /** What the state means, for tooltips and the catalogue. */
  meaning: string;
  /** The shape that tells it apart without colour. */
  shape: string;
  /** The motion it may carry, and its still twin under reduced motion. */
  motion: string;
  /** Groups in this state start collapsed. */
  collapsed: boolean;
}

export const WORK_STATE_META: Record<WorkState, WorkStateMeta> = {
  'needs-you': {
    label: 'Needs you',
    tone: 'attention',
    meaning: 'Waiting on a person: an approval, an answer or a decision.',
    shape: 'Hand on a soft amber disc',
    motion: 'The Wait moment once when it starts (a tint); still twin: the icon and label',
    collapsed: false,
  },
  failed: {
    label: 'Failed',
    tone: 'danger',
    meaning: 'Stopped with an error, or blocked. Someone should look.',
    shape: 'Cross on a soft red disc',
    motion: 'The cross fades in from 0.9 once; still twin: the cross',
    collapsed: false,
  },
  running: {
    label: 'Running',
    tone: 'working',
    meaning: 'An agent or a job is working on it now.',
    shape: 'Ring with a turning arc',
    motion: 'The arc turns (the one perpetual motion); still twin: the ring with a fixed arc',
    collapsed: false,
  },
  verifying: {
    label: 'Verifying',
    tone: 'working',
    meaning: 'Checks are running before it lands: tests, build, review.',
    shape: 'Dotted ring with a turning arc',
    motion: 'The arc turns; still twin: the dotted ring with a fixed arc',
    collapsed: false,
  },
  'waiting-for-cpu': {
    label: 'Waiting for CPU',
    tone: 'neutral',
    meaning: 'The machine is full; waiting for its turn in line.',
    shape: 'CPU chip icon',
    motion: 'None',
    collapsed: false,
  },
  queued: {
    label: 'Queued',
    tone: 'neutral',
    meaning: 'Accepted and waiting its turn. Nothing to do.',
    shape: 'Dashed ring',
    motion: 'None: queued work is at rest',
    collapsed: false,
  },
  landed: {
    label: 'Landed',
    tone: 'success',
    meaning: 'Finished and in: merged, deployed or done. Green only where landing is the news.',
    shape: 'Check in a ring',
    motion: 'The check strokes in once (Land), then settles; still twin: the check',
    collapsed: true,
  },
  stale: {
    label: 'Stale',
    tone: 'neutral',
    meaning: 'Idle or not reporting: no activity for a while.',
    shape: 'Hollow dot, faint',
    motion: 'None',
    collapsed: true,
  },
};

/** Literal class names per tone (Tailwind needs them spelled out). */
export const WORK_TONE_CLASS: Record<WorkTone, { fg: string; soft: string; bar: string }> = {
  attention: { fg: 'text-status-attention', soft: 'bg-status-attention-soft', bar: 'bg-status-attention-fill' },
  danger: { fg: 'text-status-danger', soft: 'bg-status-danger-soft', bar: 'bg-status-danger-fill' },
  working: { fg: 'text-status-working', soft: 'bg-status-working-soft', bar: 'bg-status-working-fill' },
  success: { fg: 'text-status-success', soft: 'bg-status-success-soft', bar: 'bg-status-success-fill' },
  neutral: { fg: 'text-fg-subtle', soft: 'bg-hover', bar: 'bg-fg-faint' },
};

export interface WorkGroup<T> {
  state: WorkState;
  items: T[];
  /** Starts collapsed (finished and quiet work). */
  collapsed: boolean;
}

/**
 * Items grouped by work state in the order of attention, empty groups left out.
 *
 *   groupByWorkState(checkouts, (c) => c.state)
 */
export function groupByWorkState<T>(items: readonly T[], stateOf: (item: T) => WorkState): WorkGroup<T>[] {
  const by = new Map<WorkState, T[]>();
  for (const item of items) {
    const s = stateOf(item);
    const list = by.get(s);
    if (list) list.push(item);
    else by.set(s, [item]);
  }
  return WORK_STATES.filter((s) => by.has(s)).map((state) => ({ state, items: by.get(state)!, collapsed: WORK_STATE_META[state].collapsed }));
}

/** How many items are in each state (every state present, zero when none). */
export function countWorkStates<T>(items: readonly T[], stateOf: (item: T) => WorkState): Record<WorkState, number> {
  const out = Object.fromEntries(WORK_STATES.map((s) => [s, 0])) as Record<WorkState, number>;
  for (const item of items) out[stateOf(item)] += 1;
  return out;
}
